import type Highcharts from "highcharts";
import seriesColors from "./seriesColors";
import { zIndexChart } from "@ui/Highcharts/BetterHighcharts";
import t from "../../../../i18n";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import computeUnexplainedGap from "../../../../domain/computeUnexplainedGap";
import type { Setup } from "../../../../api/useSetups";
import type { IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import type {
  FailureValueStatus,
  IndexedFailureMetadata,
} from "../../../../api/useFailureMetadata";
import type { Translation } from "../../../../api/useTranslations";
import type { MetrologyOverrides } from "../../../../api/useMetrologyOverrides";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { Quantity } from "../../../../api/useQuantities";
import { isAnalysisQuantityValid } from "../../metrology";
import { UNEXPLAINED_GAP_ID } from "./serieIds";

const fillColor: Highcharts.SeriesArearangeOptions["fillColor"] = {
  linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
  stops: [
    [0, seriesColors.unexplainedSymptom], // start
    [1, seriesColors.fadedUnexplainedSymptom], // end
  ],
};

export default function getUnexplainedGapSerie({
  analyses,
  indexedFailureValues,
  indexedManualFailureValues,
  indexedFailureMetadata,
  setups,
  quantity,
  includeSuspectedFailure,
  yAxisId,
  legendIndex,
  metrologyOverrides: _metrologyOverrides,
  translations,
}: {
  analyses: ActiveAnalysis[];
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
  indexedFailureMetadata: IndexedFailureMetadata;
  setups: Setup[];
  quantity: Quantity;
  includeSuspectedFailure: boolean;
  yAxisId: string;
  legendIndex: number;
  metrologyOverrides: MetrologyOverrides;
  translations: Translation;
}): Highcharts.SeriesArearangeOptions {
  const quantitySimulationOutputBySetupId: {
    [setupId: string]: Setup["simulationOutputs"][number];
  } = {};

  for (const setup of setups) {
    const output = setup.simulationOutputs.find(s => s.quantityId === quantity.id);
    if (output) quantitySimulationOutputBySetupId[setup.id] = output;
  }

  const status: Set<FailureValueStatus> = includeSuspectedFailure
    ? new Set(["TO_BE_ANALYSED", "SUSPECTED", "CONFIRMED"])
    : new Set(["CONFIRMED"]);

  const data: Highcharts.PointOptionsObject[] = analyses.reduce(
    (acc: Highcharts.PointOptionsObject[], analysis, index) => {
      const previousAnalysis = analyses[index - 1];
      const setup = getAnalysisSetup(setups, analysis);

      if (setup === null) {
        acc.push({
          x: analysis.date,
          high: undefined,
          low: undefined,
        });
        return acc;
      }

      if (analysis.quantityValues[quantity.id] === undefined) {
        acc.push({
          x: analysis.date,
          high: undefined,
          low: undefined,
        });
        return acc;
      }

      if (previousAnalysis !== undefined && previousAnalysis.group !== analysis.group) {
        acc.push({
          x: (previousAnalysis.date + analysis.date) / 2,
          high: undefined,
          low: undefined,
        });
      }

      const offset = quantitySimulationOutputBySetupId[setup.id]?.offsetExpected ?? 0;
      const valid = isAnalysisQuantityValid(analysis, quantity);
      acc.push({
        x: analysis.date,
        high: valid
          ? (computeUnexplainedGap(
              analysis,
              indexedFailureValues,
              indexedFailureMetadata,
              indexedManualFailureValues,
              quantity.id,
              status,
              offset,
            ) ?? undefined)
          : undefined,
        low: valid ? 0 : undefined,
      });

      return acc;
    },
    [],
  );

  return {
    id: UNEXPLAINED_GAP_ID,
    yAxis: yAxisId,
    name: t("charts.legend.unexplained_losses", translations),
    color: seriesColors.unexplainedSymptom,
    fillColor,
    type: "arearange" as const,
    replicateInNavigator: true,
    data,
    showInLegend: true,
    legendIndex,
    zIndex: zIndexChart.areaSeries + 1,
  };
}
